import axios from "@/utils/axios";
import { supabase } from "@/lib/supabase";
import { useSetState } from "@/hooks/use-set-state";
import { useMemo, useEffect, useCallback } from "react";
import { claimReferral } from "@/services/referral";
import { devError } from "@/utils/dev-console";

import { AuthContext } from "./auth-context";

import type { AuthState } from "../auth/auth-context-value";

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

const REFERRAL_STORAGE_KEY = "lint.referral.code";
const REFERRAL_EXPIRES_AT_KEY = "lint.referral.expiresAt";
const REFERRAL_CLAIMED_KEY = "lint.referral.claimed";
const REFERRAL_LAST_ATTEMPT_KEY = "lint.referral.lastClaimAttemptAt";

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        setState({ user: null, loading: false });
        devError(error);
        throw error;
      }

      if (session) {
        const accessToken = session?.access_token;

        setState({ user: { ...session, ...session?.user }, loading: false });
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

        // best-effort referral claim after authentication (silent + idempotent).
        try {
          const alreadyClaimed = localStorage.getItem(REFERRAL_CLAIMED_KEY) === "true";
          if (!alreadyClaimed) {
            const code = localStorage.getItem(REFERRAL_STORAGE_KEY) || "";
            const expiresAt = localStorage.getItem(REFERRAL_EXPIRES_AT_KEY);
            const isExpired = expiresAt ? Date.now() > new Date(expiresAt).getTime() : false;

            if (isExpired) {
              localStorage.removeItem(REFERRAL_STORAGE_KEY);
              localStorage.removeItem(REFERRAL_EXPIRES_AT_KEY);
            } else if (code.trim()) {
              const lastAttemptAt = Number(localStorage.getItem(REFERRAL_LAST_ATTEMPT_KEY) || "0");
              if (!Number.isFinite(lastAttemptAt) || Date.now() - lastAttemptAt > 60_000) {
                localStorage.setItem(REFERRAL_LAST_ATTEMPT_KEY, String(Date.now()));
                await claimReferral(code.trim());
                localStorage.setItem(REFERRAL_CLAIMED_KEY, "true");
              }
            }
          }
        } catch {
          // ignore
        }
      } else {
        setState({ user: null, loading: false });
        delete axios.defaults.headers.common.Authorization;
      }
    } catch (error) {
      devError(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user?.id,
            accessToken: state.user?.access_token,
            displayName: `${state.user?.user_metadata.display_name}`,
            role: state.user?.role ?? "admin",
          }
        : null,
      checkUserSession,
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
    }),
    [checkUserSession, state.user, status]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
